import React from 'react';
import type { Block } from '@/types';
import { BlockType } from '@/types';
import { applyVariableMapDeep } from '@/lib/resolveVariablesFromMap';
import TextBlock from './blocks/TextBlock';
import ImageBlock from './blocks/ImageBlock';
import VideoBlock from './blocks/VideoBlock';
import TableBlock from './blocks/TableBlock';
import ProductBlock from './blocks/ProductBlock';
import PricingBlock from './blocks/PricingBlock';
import DividerBlock from './blocks/DividerBlock';
import SpacerBlock from './blocks/SpacerBlock';
import SignatureBlock from './blocks/SignatureBlock';
import HeaderBlock from './blocks/HeaderBlock';
import CoverBlock from './blocks/CoverBlock';
import TeamBlock from './blocks/TeamBlock';
import ContentPageBlock from './blocks/ContentPageBlock';
import InvestmentBlock from './blocks/InvestmentBlock';
import PlanVanAanpakBlock from './blocks/PlanVanAanpakBlock';
import FooterBlock from './blocks/FooterBlock';
import SignaturePageBlock from './blocks/SignaturePageBlock';
import type { PublicQuoteLocale } from '@/lib/publicQuoteLocale';

interface BlockRendererProps {
  block: Block;
  isEditable?: boolean;
  onUpdate?: (patch: Partial<Block>) => void;
  /** Voorkeur boven resolveVariables: geen JSON.stringify/parse (veilig bij " in waarden). */
  variableMap?: Record<string, string>;
  resolveVariables?: (text: string) => string;
  boxSignStatus?: string | null;
  boxSignedAt?: string | null;
  onSignature?: (dataUrl: string | null) => void;
  existingSignature?: string | null;
  isPublic?: boolean;
  quoteStatus?: string;
  onAccept?: () => void;
  acceptLoading?: boolean;
  /** Publieke pagina met meerdere tekenbevoegden: tekenen namens één van hen. */
  onSignAsRecipient?: (recipientId: string, signatureDataUrl: string) => void;
  signingRecipientId?: string | null;
  /** Alleen voor gepubliceerde pagina: taal van knoppen rond handtekening */
  publicLocale?: PublicQuoteLocale;
  printFragmentIndex?: number;
  printFragmentCount?: number;
  /** Alleen publieke PDF: wacht tot investeringstabellen hun subpagina's hebben gemeten. */
  onInvestmentMeasureComplete?: () => void;
}

function BlockRenderer({
  block,
  isEditable = false,
  onUpdate,
  variableMap,
  resolveVariables,
  boxSignStatus,
  boxSignedAt,
  onSignature,
  existingSignature,
  isPublic = false,
  quoteStatus,
  onAccept,
  acceptLoading,
  onSignAsRecipient,
  signingRecipientId,
  publicLocale,
  printFragmentIndex = 0,
  printFragmentCount = 1,
  onInvestmentMeasureComplete,
}: BlockRendererProps) {
  let resolvedContent = block.content;
  if (variableMap && Object.keys(variableMap).length > 0) {
    resolvedContent = applyVariableMapDeep(block.content, variableMap) as Block['content'];
  } else if (resolveVariables) {
    try {
      resolvedContent = JSON.parse(resolveVariables(JSON.stringify(block.content)));
    } catch {
      resolvedContent = block.content;
    }
  }
  const displayBlock = { ...block, content: resolvedContent };

  const renderBlock = () => {
    switch (displayBlock.type) {
      case BlockType.TEXT:
        return <TextBlock block={displayBlock} isEditable={isEditable} onUpdate={onUpdate} />;
      case BlockType.IMAGE:
        return <ImageBlock block={displayBlock} isEditable={isEditable} onUpdate={onUpdate} />;
      case BlockType.VIDEO:
        return <VideoBlock block={displayBlock} />;
      case BlockType.TABLE:
        return <TableBlock block={displayBlock} isEditable={isEditable} onUpdate={onUpdate} />;
      case BlockType.PRODUCT:
        return <ProductBlock block={displayBlock} />;
      case BlockType.PRICING:
        return <PricingBlock block={displayBlock} isEditable={isEditable} onUpdate={onUpdate} />;
      case BlockType.DIVIDER:
        return <DividerBlock block={displayBlock} isEditable={isEditable} onUpdate={onUpdate} />;
      case BlockType.SPACER:
        return <SpacerBlock block={displayBlock} isEditable={isEditable} onUpdate={onUpdate} />;
      case BlockType.SIGNATURE:
        return <SignatureBlock block={displayBlock} isEditable={isEditable} onUpdate={onUpdate} boxSignStatus={boxSignStatus} boxSignedAt={boxSignedAt} />;
      case BlockType.HEADER:
        return <HeaderBlock block={displayBlock} isEditable={isEditable} onUpdate={onUpdate} />;
      case BlockType.COVER:
        return <CoverBlock block={displayBlock} isEditable={isEditable} onUpdate={onUpdate} />;
      case BlockType.TEAM:
        return <TeamBlock block={displayBlock} />;
      case BlockType.CONTENT_PAGE:
        return (
          <ContentPageBlock
            block={displayBlock}
            isPublic={isPublic}
            isContinuation={isPublic && printFragmentCount > 1 && printFragmentIndex > 0}
          />
        );
      case BlockType.INVESTMENT:
        return (
          <InvestmentBlock
            block={displayBlock}
            isPublic={isPublic}
            onMeasureComplete={onInvestmentMeasureComplete}
          />
        );
      case BlockType.PLAN_VAN_AANPAK:
        return (
          <PlanVanAanpakBlock
            block={displayBlock}
            isEditable={isEditable}
            onUpdate={onUpdate}
            isPublic={isPublic}
            printFragmentIndex={printFragmentIndex}
            printFragmentCount={printFragmentCount}
          />
        );
      case BlockType.FOOTER:
        return <FooterBlock block={displayBlock} isEditable={isEditable} onUpdate={onUpdate} />;
      case BlockType.SIGNATURE_PAGE:
        return (
          <SignaturePageBlock
            block={displayBlock}
            isEditable={isEditable}
            onUpdate={onUpdate}
            boxSignStatus={boxSignStatus}
            boxSignedAt={boxSignedAt}
            onSignature={onSignature}
            existingSignature={existingSignature}
            isPublic={isPublic}
            quoteStatus={quoteStatus}
            onAccept={onAccept}
            acceptLoading={acceptLoading}
            onSignAsRecipient={onSignAsRecipient}
            signingRecipientId={signingRecipientId}
            publicLocale={publicLocale}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="h-full" style={{ display: 'flex', flex: 1, flexDirection: 'column', ...block.styles as React.CSSProperties }}>
      {renderBlock()}
    </div>
  );
}

export default BlockRenderer;
